// @ts-check
// Sección de enunciado del ejercicio: título, descripción, ejemplos, estado de
// carga y aviso de error. La usan las tres páginas de editor (diagramas,
// pseudocódigo y Python).
//
// ## Por qué existe
//
// Estaba reimplementada tres veces, casi línea por línea:
// `diagram-editor-page.js#crearSeccionEnunciado` (~68 líneas),
// `pseudocode-editor-page.js#crearSeccionEnunciadoEjercicio` (~80) y
// `python-editor-page.js#crearSeccionEnunciado` (~66). Con ellas viajaban
// duplicadas dos decisiones de accesibilidad que NO son obvias y que un cuarto
// copia-y-pega perdería:
//
//   1. Cuando no hay ejercicio que mostrar, el `<h2>` se OCULTA en vez de
//      vaciarse. Un `<h2>` vacío es una violación de la regla `empty-heading`
//      de axe-core: un encabezado sin nombre accesible que el lector de
//      pantalla anuncia igualmente.
//   2. Un error de carga REABRE el `<details>` si el alumno lo había
//      colapsado. Un `role="alert"` dentro de un disclosure cerrado no se
//      anuncia: el aviso se perdería justo cuando más falta hace.
//
// El aviso de error también divergió estando triplicado: hasta el 2026-08-05
// diagramas lo pintaba con un estilo de leyenda gris (heredado de un selector
// muerto agrupado por coma) y Python no tenía NINGUNA regla para él. Por eso
// esa regla concreta vive ahora en `exercise-statement.css`, una sola vez.
//
// ## Los ejemplos (decisión del usuario, 2026-09-11)
//
// Cada ejercicio trae ejemplos (`contenidoPedagogico.ejemplos`, del tipo
// "Entrada: 5 -> Salida: Tarifa: gratuita") que hasta ese día no se enseñaban
// en ningún sitio: la salida esperada solo se veía DESPUÉS de corregir, así
// que el alumno descubría el formato exacto gastando un intento. Ahora se
// enseñan junto al enunciado, con la salida en un `<pre>` para que se vean
// sus saltos de línea tal cual.
//
// Que cada ejemplo sea VERDAD (su salida es la de un caso de prueba real, sin
// comillas ni explicaciones pegadas) lo exige
// `backend/tests/unit/test_ejemplos_ejercicios.py`, que además comprueba que
// `utils/formato-ejemplo.js` parte los ejemplos igual que el backend. Un
// ejemplo que no siga el formato se enseña tal cual, entero.
//
// ## Qué NO hace
//
// Nada de red. `mostrarError` recibe el mensaje YA traducido -- igual que
// `components/exercise-selector.js#mostrarError` -- porque traducir un
// `ApiError` a texto exige conocer `services/api-errors.js`, y
// docs/03-frontend.md §4.2 prohíbe `components -> services`. Esa traducción,
// que también estaba triplicada, vive en
// `pages/exercise-statement-errors.js`.
import './base/chips.css';
import './exercise-statement.css';
import { partirEjemplo } from '../utils/formato-ejemplo.js';

/**
 * @typedef {{
 *   elemento: HTMLElement,
 *   mostrarCargando: () => void,
 *   mostrarEjercicio: (ejercicio: import('../services/exercise-client.js').EjercicioPublico) => void,
 *   mostrarError: (mensaje: string) => void,
 * }} SeccionEnunciado
 */

/**
 * @typedef {{
 *   prefijoClase: string,
 *   colapsable?: boolean,
 *   conMeta?: boolean,
 *   metaEnChips?: boolean,
 *   claseRaiz?: string,
 * }} OpcionesSeccionEnunciado
 */

/**
 * Crea la sección accesible que muestra (o anuncia la carga/el error de
 * cargar) el enunciado de un ejercicio. Empieza oculta: solo se hace visible
 * cuando hay algo que mostrar, para que un editor abierto sin ejercicio no
 * enseñe un hueco vacío.
 *
 * Los tres parámetros existen porque las tres páginas NO tenían el mismo
 * enunciado, y unificarlas a la fuerza habría cambiado lo que ve el alumno:
 *
 * - `prefijoClase`: raíz de los nombres de clase (`exercise-statement`,
 *   `pseudocode-exercise-statement`, `python-exercise-statement`). Cada página
 *   conserva los suyos para seguir pintando su propio acento de color y su
 *   `grid-area` sin que una hoja de página toque la de otra. El `id` del `<h2>`
 *   se deriva del prefijo, que es justo lo que ya hacían las tres.
 * - `colapsable`: envuelve la sección en un `<details open>` con
 *   `<summary>Enunciado</summary>` (DEC-086). Pseudocódigo y Python lo
 *   llevan; diagramas NO, y añadírselo aquí sería un cambio visible que
 *   ningún refactor debería colar.
 * - `conMeta`: línea "Dificultad · Categoría". La tienen diagramas y
 *   pseudocódigo; Python no. Misma razón: añadirla se ve.
 *
 * Los ejemplos, en cambio, son iguales en las tres (clases compartidas
 * `exercise-statement-example*`, en `exercise-statement.css`).
 *
 * Rediseño (Fase 4b), cada página los activa al llegarle su turno:
 * - `metaEnChips`: la dificultad y la categoría, como dos chips de las piezas
 *   base. El texto no cambia: el « · » que las une sigue ahí, oculto solo a
 *   la vista.
 * - `claseRaiz`: clases extra para el envoltorio exterior (el `<details>` si
 *   lo hay), p. ej. `ui-panel`.
 *
 * @param {OpcionesSeccionEnunciado} opciones
 * @returns {SeccionEnunciado}
 */
export function crearSeccionEnunciado({
  prefijoClase,
  colapsable = false,
  conMeta = false,
  metaEnChips = false,
  claseRaiz,
}) {
  const idEncabezado = `${prefijoClase}-heading`;

  const seccion = document.createElement('section');
  seccion.className = prefijoClase;
  seccion.hidden = true;
  seccion.setAttribute('aria-labelledby', idEncabezado);

  const encabezado = document.createElement('h2');
  encabezado.id = idEncabezado;
  encabezado.className = `${prefijoClase}-heading`;

  const descripcion = document.createElement('p');
  descripcion.className = `${prefijoClase}-description`;

  const ejemplos = document.createElement('div');
  ejemplos.className = 'exercise-statement-examples';
  ejemplos.hidden = true;
  const tituloEjemplos = document.createElement('h3');
  tituloEjemplos.className = 'exercise-statement-examples-heading';
  const avisoEjemplos = document.createElement('p');
  avisoEjemplos.className = 'exercise-statement-examples-hint';
  avisoEjemplos.textContent = 'Tu programa tiene que mostrar la salida con este mismo formato.';
  const listaEjemplos = document.createElement('ul');
  listaEjemplos.className = 'exercise-statement-examples-list';
  ejemplos.append(tituloEjemplos, avisoEjemplos, listaEjemplos);

  const meta = document.createElement('p');
  meta.className = `${prefijoClase}-meta`;
  meta.hidden = true;

  const estado = document.createElement('p');
  estado.className = `${prefijoClase}-status`;
  estado.setAttribute('role', 'status');
  estado.hidden = true;

  const error = document.createElement('p');
  // Dos clases a propósito: la de la página (que puede seguir ajustando lo
  // suyo) y la compartida `exercise-statement-error`, que es la que trae los
  // tokens de peligro desde `exercise-statement.css`. Con una sola por página
  // fue con lo que los tres avisos acabaron divergiendo.
  error.className = `${prefijoClase}-error`;
  error.classList.add('exercise-statement-error'); // idempotente si ya la tiene
  error.setAttribute('role', 'alert');
  error.hidden = true;

  seccion.append(encabezado, descripcion, ejemplos, ...(conMeta ? [meta] : []), estado, error);

  /** Envoltorio externo: el `<details>` si la página lo quiere, o la propia sección. */
  let raiz = seccion;
  /** @type {HTMLDetailsElement | null} */
  let details = null;

  if (colapsable) {
    // DEC-086: disclosure NATIVO -- estado abierto/cerrado y operación por
    // teclado sin una línea de ARIA manual. La `<section>` conserva clase, rol
    // y `aria-labelledby`; el `<details>` solo controla la visibilidad
    // exterior y NUNCA se colapsa solo (no se toca `.open` salvo ante error).
    details = document.createElement('details');
    details.className = `${prefijoClase}-details`;
    details.open = true;
    details.hidden = true;

    const resumen = document.createElement('summary');
    resumen.className = `${prefijoClase}-summary`;
    // Clase compartida: su aspecto en el rediseño vive una sola vez en
    // `exercise-statement.css` (idempotente si ya es la de la página).
    resumen.classList.add('exercise-statement-summary');
    resumen.textContent = 'Enunciado';

    details.append(resumen, seccion);
    raiz = details;
  }
  if (claseRaiz) raiz.classList.add(...claseRaiz.split(/\s+/).filter(Boolean));

  /** Hace visible el envoltorio (el `<details>` si lo hay) y la sección. */
  function mostrarRaiz() {
    raiz.hidden = false;
    seccion.hidden = false;
  }

  /**
   * Pinta los ejemplos del ejercicio, siempre como TEXTO (`textContent`): los
   * sirve el backend y se tratan como no confiables (03-frontend §8.2). Sin
   * ejemplos, el bloque entero se oculta (un `<h3>` vacío sería una violación
   * de `empty-heading`, como el `<h2>`).
   * @param {unknown} lista
   */
  function pintarEjemplos(lista) {
    listaEjemplos.replaceChildren();
    const textos = Array.isArray(lista)
      ? lista.filter((texto) => typeof texto === 'string' && texto.trim() !== '')
      : [];
    ejemplos.hidden = textos.length === 0;
    tituloEjemplos.textContent = textos.length === 1 ? 'Ejemplo' : 'Ejemplos';
    for (const texto of textos) {
      const item = document.createElement('li');
      item.className = 'exercise-statement-example';
      const partes = partirEjemplo(texto);
      if (partes) {
        const entrada = document.createElement('p');
        entrada.className = 'exercise-statement-example-input';
        entrada.textContent = `Entrada: ${partes.entrada}`;
        const etiquetaSalida = document.createElement('p');
        etiquetaSalida.className = 'exercise-statement-example-label';
        etiquetaSalida.textContent = 'Salida:';
        const salida = document.createElement('pre');
        salida.className = 'exercise-statement-example-output';
        salida.textContent = partes.salida;
        item.append(entrada, etiquetaSalida, salida);
      } else {
        item.textContent = texto;
      }
      listaEjemplos.append(item);
    }
  }

  /**
   * Deja el bloque "título + descripción + ejemplos + meta" sin contenido Y
   * OCULTO. Ocultar, no solo vaciar: un `<h2>` vacío dispara la regla
   * `empty-heading` de axe-core (encabezado sin nombre accesible).
   */
  function vaciarEjercicio() {
    encabezado.hidden = true;
    encabezado.textContent = '';
    descripcion.hidden = true;
    descripcion.textContent = '';
    pintarEjemplos([]);
    meta.hidden = true;
    meta.textContent = '';
  }

  function mostrarCargando() {
    mostrarRaiz();
    vaciarEjercicio();
    error.hidden = true;
    error.textContent = '';
    estado.hidden = false;
    estado.textContent = 'Cargando enunciado del ejercicio…';
  }

  /** @param {import('../services/exercise-client.js').EjercicioPublico} ejercicio */
  function mostrarEjercicio(ejercicio) {
    mostrarRaiz();
    estado.hidden = true;
    estado.textContent = '';
    error.hidden = true;
    error.textContent = '';
    encabezado.hidden = false;
    encabezado.textContent = ejercicio.titulo;
    descripcion.hidden = false;
    descripcion.textContent = ejercicio.descripcion;
    pintarEjemplos(ejercicio.contenidoPedagogico?.ejemplos);
    if (conMeta && (ejercicio.dificultad || ejercicio.categoria)) {
      meta.hidden = false;
      const dificultad = `Dificultad: ${ejercicio.dificultad}`;
      const categoria = `Categoría: ${ejercicio.categoria}`;
      if (metaEnChips) {
        const separador = document.createElement('span');
        separador.className = 'u-visually-hidden';
        separador.textContent = ' · ';
        meta.replaceChildren(chip(dificultad), separador, chip(categoria));
      } else {
        meta.textContent = `${dificultad} · ${categoria}`;
      }
    } else {
      meta.hidden = true;
      meta.textContent = '';
    }
  }

  /** @param {string} mensaje mensaje ya traducido, nunca el error técnico crudo */
  function mostrarError(mensaje) {
    mostrarRaiz();
    // Un error de carga es información crítica: si el alumno había colapsado
    // el enunciado, se REABRE. Un `role="alert"` dentro de un `<details>`
    // cerrado no llega a anunciarse.
    if (details) details.open = true;
    estado.hidden = true;
    estado.textContent = '';
    vaciarEjercicio();
    error.hidden = false;
    error.textContent = mensaje;
  }

  return { elemento: raiz, mostrarCargando, mostrarEjercicio, mostrarError };
}

/**
 * Un chip de las piezas base, siempre como texto (lo sirve el backend).
 * @param {string} texto
 * @returns {HTMLElement}
 */
function chip(texto) {
  const elemento = document.createElement('span');
  elemento.className = 'ui-chip';
  elemento.textContent = texto;
  return elemento;
}
